import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useI18n } from "@wato/i18n/react";
import { Button } from "@wato/ui";
import { useApi } from "../../api/context";
import { useAuth } from "../../auth/AuthProvider";
import { getSupportToken } from "./support-session";

/**
 * OWN-85: "Hand over my shift" in the avatar menu. Pins a brief on each of the agent's open
 * threads and notifies the chosen colleague; threads move only when the box is ticked.
 */
export function HandoverMenu({ workspaceId }: { workspaceId: string }) {
  const { t } = useI18n();
  const api = useApi();
  const { state } = useAuth();
  const userId = state.status === "ready" ? state.me.user.id : null;
  const [open, setOpen] = useState(false);
  const [toUserId, setToUserId] = useState("");
  const [reassign, setReassign] = useState(false);
  const members = useQuery({
    queryKey: ["assignable-members", workspaceId],
    queryFn: () => api.listAssignableMembers(workspaceId),
    enabled: open && userId !== null,
  });
  const send = useMutation({
    mutationFn: () =>
      api.handoverShift(workspaceId, {
        fromUserId: userId!,
        ...(toUserId ? { toUserId, reassign } : {}),
      }),
  });
  if (userId === null || getSupportToken()) return null;
  const receivers = (members.data ?? []).filter((m) => m.userId !== userId);
  return (
    <div className="border-t border-line px-3 py-2">
      <Button size="sm" variant="secondary" aria-expanded={open} onClick={() => setOpen(!open)}>
        {t("opsOwn.handover.button")}
      </Button>
      {open ? (
        <div className="mt-2 flex flex-col gap-2 text-sm" data-testid="handover-form">
          <label className="flex flex-col gap-1">
            <span className="text-xs font-semibold text-fg-muted">
              {t("opsOwn.handover.receiver")}
            </span>
            <select
              className="rounded-md border border-line bg-surface px-2 py-1"
              value={toUserId}
              onChange={(e) => {
                setToUserId(e.target.value);
                if (!e.target.value) setReassign(false);
              }}
            >
              <option value="">{t("opsOwn.handover.noReceiver")}</option>
              {receivers.map((m) => (
                <option key={m.userId} value={m.userId}>
                  {m.label}
                </option>
              ))}
            </select>
          </label>
          <label className="flex items-center gap-2">
            <input
              type="checkbox"
              checked={reassign}
              disabled={!toUserId}
              onChange={(e) => setReassign(e.target.checked)}
            />
            <span>{t("opsOwn.handover.reassign")}</span>
          </label>
          <Button size="sm" disabled={send.isPending} onClick={() => send.mutate()}>
            {t("opsOwn.handover.send")}
          </Button>
          {send.isSuccess ? (
            <p role="status" className="text-xs text-fg-muted">
              {t("opsOwn.handover.done", { open: send.data.brief.totals.open })}
            </p>
          ) : null}
          {send.isError ? (
            <p role="alert" className="text-xs text-danger">
              {t("opsOwn.handover.failed")}
            </p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
